import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Award, ArrowLeft, Heart } from "lucide-react";
import { api, API } from "@/lib/api";
import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { Overline } from "@/components/Cards";
import { HERO_MURAL } from "@/lib/constants";

const MEDALS = ["#FFD700", "#C0C0C0", "#CD7F32"];

function fmtMonth(iso) {
  try { return new Date(iso).toLocaleDateString("en-US", { month: "long", year: "numeric" }); }
  catch { return ""; }
}

export default function WinnersHall() {
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/contest/winners").then((r) => setRounds(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <Seo title="Contest Winners Hall" description="Celebrate the winning RiNo photos and video shorts from past Visit RiNo photo contest rounds." image={HERO_MURAL} />
      <PageHeader overline="Hall of Fame" title="Winners Hall" subtitle="The best shots of RiNo — mural by mural, pour by pour. Congrats to every winner who captured the district's energy." image={HERO_MURAL} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <Link to="/contest" data-testid="back-to-contest" className="inline-flex items-center gap-2 text-[#00E5FF] text-sm font-accent hover:underline mb-8"><ArrowLeft size={15} /> Back to the contest</Link>

        {loading ? (
          <div className="text-neutral-500 py-16 text-center">Loading…</div>
        ) : rounds.length === 0 ? (
          <div className="text-neutral-500 py-16 text-center border border-white/10" data-testid="winners-empty">
            <Award size={28} className="text-neutral-600 mx-auto mb-3" />
            No rounds have wrapped up yet. Winners appear here the moment a round closes.
          </div>
        ) : (
          <div className="space-y-16">
            {rounds.map((r) => (
              <div key={r.id} data-testid={`winners-round-${r.id}`}>
                <div className="flex items-center gap-3 mb-6">
                  <Trophy size={20} className="text-[#FFEA00]" />
                  <div>
                    <Overline>{fmtMonth(r.ends_at)}</Overline>
                    <h2 className="font-display font-extrabold text-2xl text-white mt-1">{r.title}</h2>
                    {r.theme && <p className="text-neutral-400 text-sm">Theme: {r.theme}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {(r.winners || []).map((e, i) => (
                    <div key={e.id} className="border border-white/10 bg-[#141414] relative" data-testid={`winner-entry-${e.id}`}>
                      <span className="absolute top-3 left-3 z-10 text-black text-xs font-accent font-bold px-2.5 py-1 flex items-center gap-1" style={{ background: MEDALS[i] || "#FFEA00" }}>
                        <Trophy size={11} /> {i === 0 ? "1st" : i === 1 ? "2nd" : i === 2 ? "3rd" : "Winner"}
                      </span>
                      <div className="relative h-56 overflow-hidden bg-black">
                        {e.media_type === "video"
                          ? <video src={`${API}/images/${e.image_id}/file`} className="w-full h-full object-cover" controls />
                          : <img src={`${API}/images/${e.image_id}/file`} alt={e.caption} className="w-full h-full object-cover" />}
                      </div>
                      <div className="p-4">
                        <div className="text-white text-sm font-medium truncate">{e.caption || e.spot || "RiNo"}</div>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-neutral-500 text-xs truncate">by {e.name}</span>
                          <span className="flex items-center gap-1 text-xs text-[#FF0055]"><Heart size={13} fill="#FF0055" /> {e.likes}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
